import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
  { name: "Home", path: "/", testid: "nav-home-link" },
  { name: "About", path: "/about", testid: "nav-about-link" },
  { name: "Founder", path: "/founder", testid: "nav-founder-link" },
  { name: "Services", path: "/services", testid: "nav-services-link" },
  { name: "Industries", path: "/industries", testid: "nav-industries-link" },
  { name: "Contact", path: "/contact", testid: "nav-contact-link" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-header"
      className="fixed top-9 inset-x-0 z-50 backdrop-blur-xl bg-[#0A0D0F]/80 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" aria-label="Green Planet Technologies home" onClick={() => setOpen(false)}>
          <Logo compact />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={l.testid}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  isActive ? "text-lime" : "text-gray-400 hover:text-white"
                }`
              }
            >
              {l.name}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="hidden md:inline-flex items-center gap-2 bg-lime text-obsidian font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-lime-bright hover:shadow-[0_0_24px_rgba(132,204,22,0.4)] transition-all duration-300"
          >
            Request Consultation
            <ArrowUpRight size={16} />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname}
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-[#0A0D0F] border-b border-white/10 px-6 py-6 flex flex-col gap-5"
            aria-label="Mobile"
          >
            {LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={`mobile-${l.testid}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `font-display text-2xl font-bold tracking-tight ${
                    isActive ? "text-lime" : "text-white"
                  }`
                }
              >
                {l.name}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-header-cta-button"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-lime text-obsidian font-semibold px-5 py-3 rounded-full"
            >
              Request Consultation <ArrowUpRight size={16} />
            </Link>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
